/**
 * Turnstile in the page (SEC-002): run Cloudflare's bot check when, and only when, the
 * server asks for it, which is a brand-new player's first counting shot.
 *
 * Cloudflare's script is loaded on demand, not with the page, so a returning player
 * never downloads it. It is the one outside script the page may load, allowed by exact
 * origin in the CSP (script-src and frame-src https://challenges.cloudflare.com; never
 * a wildcard, never 'unsafe-inline'). The widget is set to Invisible in the dashboard
 * and asked to appear only if it truly needs the person (`interaction-only`), so in
 * practice nobody sees anything (D-039).
 *
 * The token goes to our server, which checks it with Cloudflare (apps/api/src/challenge.ts).
 * Nothing here decides anything: a missing or failed check just means the shots wait.
 */
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** How long a check may take before the shots go back to waiting. */
const CHALLENGE_TIMEOUT_MS = 30_000;

interface TurnstileApi {
  render(host: HTMLElement, options: Record<string, unknown>): string | null | undefined;
  remove(widgetId: string): void;
}

/** `window.turnstile`, narrowed from unknown: it is someone else's code. */
function readApi(): TurnstileApi | undefined {
  const api: unknown = (window as unknown as Record<string, unknown>)['turnstile'];
  if (typeof api !== 'object' || api === null) return undefined;
  const { render, remove } = api as Record<string, unknown>;
  return typeof render === 'function' && typeof remove === 'function'
    ? (api as TurnstileApi)
    : undefined;
}

let loading: Promise<TurnstileApi | undefined> | undefined;

function loadTurnstile(): Promise<TurnstileApi | undefined> {
  loading ??= new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.addEventListener('load', () => {
      resolve(readApi());
    });
    script.addEventListener('error', () => {
      // Blocked or offline: allow a later attempt to load it again.
      loading = undefined;
      script.remove();
      resolve(undefined);
    });
    document.head.append(script);
  });
  return loading;
}

/** Run the check with the server's site key; the token, or undefined if it did not pass. */
export async function runChallenge(siteKey: string): Promise<string | undefined> {
  const api = await loadTurnstile();
  if (api === undefined) return undefined;
  const host = document.createElement('div');
  host.className = 'challenge';
  document.body.append(host);

  return new Promise((resolve) => {
    let widgetId: string | null | undefined;
    let settled = false;
    const done = (token: string | undefined): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        if (typeof widgetId === 'string') api.remove(widgetId);
      } catch {
        // Already gone.
      }
      host.remove();
      resolve(token);
    };
    const timer = setTimeout(() => {
      done(undefined);
    }, CHALLENGE_TIMEOUT_MS);
    try {
      widgetId = api.render(host, {
        sitekey: siteKey,
        action: 'new-player',
        appearance: 'interaction-only',
        // No hidden form field: the token goes straight to our code, then our server.
        'response-field': false,
        callback: (token: unknown) => {
          done(typeof token === 'string' && token.length > 0 ? token : undefined);
        },
        'error-callback': () => {
          done(undefined);
        },
        'expired-callback': () => {
          done(undefined);
        },
        'timeout-callback': () => {
          done(undefined);
        },
      });
    } catch {
      done(undefined);
    }
  });
}
